"use client";

import { useEffect, useRef } from "react";
import * as THREE from "three";

/**
 * The hero's background figure: a field of contour lines whose profile is
 * displaced by layered waves — a ridgeline plot given depth.
 *
 * Displacement happens entirely in the vertex shader, so the per-frame cost on
 * the main thread is a single uniform write. The render loop is suspended when
 * the canvas scrolls out of view or the tab is hidden.
 */

const VERTEX = /* glsl */ `
  uniform float uTime;
  uniform float uAmp;

  varying float vHeight;
  varying float vDepth;

  // Four offset waves — enough interference to never read as a single sine,
  // cheap enough to run per vertex on integrated graphics.
  float surface(vec2 p, float t) {
    float h = 0.0;
    h += sin(p.x * 0.55 + t * 0.40) * 0.62;
    h += sin(p.y * 0.78 - t * 0.29) * 0.44;
    h += sin((p.x + p.y) * 0.41 + t * 0.23) * 0.38;
    h += sin(length(p) * 0.86 - t * 0.52) * 0.32;
    return h;
  }

  void main() {
    vec3 pos = position;
    vec2 p = pos.xz;

    // Settle the field towards flat at the edges so it reads as a considered
    // form rather than a rectangle of noise.
    float falloff = 1.0 - smoothstep(3.0, 13.0, length(p));
    float h = surface(p, uTime) * uAmp * falloff;

    pos.y += h;
    vHeight = h;

    vec4 mv = modelViewMatrix * vec4(pos, 1.0);
    vDepth = -mv.z;
    gl_Position = projectionMatrix * mv;
  }
`;

const FRAGMENT = /* glsl */ `
  precision mediump float;

  uniform vec3 uBase;
  uniform vec3 uAccent;
  uniform float uFogNear;
  uniform float uFogFar;
  uniform float uOpacity;

  varying float vHeight;
  varying float vDepth;

  void main() {
    // Brass finds the crests, navy holds the troughs.
    float peak = smoothstep(0.10, 1.00, vHeight);
    vec3 color = mix(uBase, uAccent, peak);

    float fade = 1.0 - smoothstep(uFogNear, uFogFar, vDepth);
    float alpha = fade * mix(0.16, 0.85, peak) * uOpacity;

    if (alpha < 0.008) discard;
    gl_FragColor = vec4(color, alpha);
  }
`;

function buildGeometry(rows: number, cols: number, width: number, depth: number) {
  const segmentsPerRow = cols - 1;
  const positions = new Float32Array(rows * segmentsPerRow * 2 * 3);

  let i = 0;
  for (let r = 0; r < rows; r++) {
    const z = -depth / 2 + (r / (rows - 1)) * depth;

    for (let c = 0; c < segmentsPerRow; c++) {
      const x0 = -width / 2 + (c / segmentsPerRow) * width;
      const x1 = -width / 2 + ((c + 1) / segmentsPerRow) * width;

      positions[i++] = x0;
      positions[i++] = 0;
      positions[i++] = z;
      positions[i++] = x1;
      positions[i++] = 0;
      positions[i++] = z;
    }
  }

  const geometry = new THREE.BufferGeometry();
  geometry.setAttribute("position", new THREE.BufferAttribute(positions, 3));
  return geometry;
}

export function RidgeField({ className }: { className?: string }) {
  const hostRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const host = hostRef.current;
    if (!host) return;

    const reduced = window.matchMedia("(prefers-reduced-motion: reduce)").matches;
    const compact = window.matchMedia("(max-width: 48rem)").matches;

    let renderer: THREE.WebGLRenderer;
    try {
      renderer = new THREE.WebGLRenderer({
        antialias: !compact,
        alpha: true,
        powerPreference: "high-performance",
      });
    } catch {
      // No WebGL — the hero's CSS background stands on its own.
      return;
    }

    renderer.setClearColor(0x000000, 0);
    renderer.setPixelRatio(Math.min(window.devicePixelRatio, compact ? 1.5 : 2));
    renderer.domElement.setAttribute("aria-hidden", "true");
    host.appendChild(renderer.domElement);

    const scene = new THREE.Scene();
    const camera = new THREE.PerspectiveCamera(38, 1, 0.1, 100);
    camera.position.set(0, 2.75, 9.4);
    camera.lookAt(0, 0.15, -1.5);

    const geometry = buildGeometry(
      compact ? 30 : 46,
      compact ? 90 : 150,
      26,
      22,
    );

    const uniforms = {
      uTime: { value: 0 },
      uAmp: { value: compact ? 0.72 : 0.85 },
      uBase: { value: new THREE.Color("#276a86") },
      uAccent: { value: new THREE.Color("#36cafc") },
      uFogNear: { value: 6.0 },
      uFogFar: { value: 26.0 },
      uOpacity: { value: 0 },
    };

    const material = new THREE.ShaderMaterial({
      uniforms,
      vertexShader: VERTEX,
      fragmentShader: FRAGMENT,
      transparent: true,
      depthWrite: false,
      blending: THREE.AdditiveBlending,
    });

    const field = new THREE.LineSegments(geometry, material);
    field.rotation.y = -0.06;
    scene.add(field);

    const resize = () => {
      const { clientWidth: w, clientHeight: h } = host;
      if (!w || !h) return;
      renderer.setSize(w, h, false);
      camera.aspect = w / h;
      camera.updateProjectionMatrix();
    };
    resize();

    const resizeObserver = new ResizeObserver(resize);
    resizeObserver.observe(host);

    // Pointer parallax, eased towards the target rather than snapped to it.
    const pointer = { x: 0, y: 0 };
    const target = { x: 0, y: 0 };

    const onPointerMove = (event: PointerEvent) => {
      if (event.pointerType !== "mouse") return;
      target.x = (event.clientX / window.innerWidth - 0.5) * 2;
      target.y = (event.clientY / window.innerHeight - 0.5) * 2;
    };
    window.addEventListener("pointermove", onPointerMove, { passive: true });

    let visible = true;
    const intersectionObserver = new IntersectionObserver(
      ([entry]) => {
        visible = entry.isIntersecting;
        if (visible && !reduced) start();
      },
      { threshold: 0 },
    );
    intersectionObserver.observe(host);

    const onVisibility = () => {
      if (document.visibilityState === "visible" && visible && !reduced) start();
    };
    document.addEventListener("visibilitychange", onVisibility);

    let frame = 0;
    // Elapsed time is accumulated rather than read from the wall clock, so a
    // pause off-screen resumes where it left off instead of jumping.
    let elapsed = 0;
    let last = 0;

    const draw = (time: number) => {
      uniforms.uTime.value = time;

      // Fade the field in once the first frames are on screen.
      uniforms.uOpacity.value = Math.min(1, uniforms.uOpacity.value + 0.02);

      pointer.x += (target.x - pointer.x) * 0.045;
      pointer.y += (target.y - pointer.y) * 0.045;

      camera.position.x = pointer.x * 0.85;
      camera.position.y = 2.75 - pointer.y * 0.4;
      camera.lookAt(0, 0.15, -1.5);

      renderer.render(scene, camera);
    };

    const tick = (now: number) => {
      if (!visible || document.visibilityState === "hidden") {
        frame = 0;
        return;
      }

      elapsed += Math.min((now - last) / 1000, 0.05);
      last = now;

      draw(elapsed);
      frame = requestAnimationFrame(tick);
    };

    const start = () => {
      if (frame) return;
      last = performance.now();
      frame = requestAnimationFrame(tick);
    };

    if (reduced) {
      // Hold a single composed frame — the form without the movement.
      uniforms.uOpacity.value = 1;
      draw(12.4);
    } else {
      start();
    }

    return () => {
      cancelAnimationFrame(frame);
      resizeObserver.disconnect();
      intersectionObserver.disconnect();
      window.removeEventListener("pointermove", onPointerMove);
      document.removeEventListener("visibilitychange", onVisibility);
      geometry.dispose();
      material.dispose();
      renderer.dispose();
      renderer.domElement.remove();
    };
  }, []);

  return <div ref={hostRef} className={className} aria-hidden="true" />;
}
